import {
  doc,
  query,
  where,
  getDoc,
  getDocs,
  addDoc,
  collection,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "./firebase";

const LOCAL_STORE_KEY = `colourview-metaverse-flying-islands`;
const flyingIslandsCollection = "flyingIslands";

const isOnline = () => navigator.onLine;

const saveToLocalStorage = async (islandData: any) => {
  try {
    const existingData = localStorage.getItem(LOCAL_STORE_KEY);
    const offlineData = existingData ? JSON.parse(existingData) : [];

    offlineData.push({
      ...islandData,
      _isOffline: true,
      _createdAt: new Date().toISOString(),
    });

    localStorage.setItem(LOCAL_STORE_KEY, JSON.stringify(offlineData));
    return { success: true, isOffline: true };
  } catch (error) {
    console.error("[FlyingIslands] LocalStorage save failed:", error);
    return { success: false };
  }
};

const syncFlyingIslands = async () => {
  if (!isOnline()) return;

  try {
    const localStoreKey = localStorage.getItem(LOCAL_STORE_KEY) || "[]";
    const offlineData = JSON.parse(localStoreKey);
    for (const island of offlineData.filter((i: any) => i._isOffline)) {
      await saveToFirebase(island);
    }
    localStorage.setItem(
      LOCAL_STORE_KEY,
      JSON.stringify(offlineData.filter((i: any) => !i._isOffline))
    );
  } catch (error) {
    console.error("[FlyingIslands] Sync failed:", error);
  }
};

const saveToFirebase = async (islandData: any) => {
  try {
    const newIslandRef = await addDoc(collection(db, flyingIslandsCollection), {
      ...islandData,
      createdAt: serverTimestamp(),
    });
    return { success: true, id: newIslandRef.id };
  } catch (error) {
    console.error("[FlyingIslands] Firebase save failed:", error);
    throw error;
  }
};

export const createFlyingIsland = async (islandData: any) => {
  try {
    if (isOnline()) {
      return await saveToFirebase(islandData);
    } else {
      return await saveToLocalStorage(islandData);
    }
  } catch (error) {
    console.error("[FlyingIslands] Create failed:", error);
    return { success: false };
  }
};

export const getUserIslands = async (userId: string) => {
  try {
    if (isOnline()) {
      const q = query(
        collection(db, flyingIslandsCollection),
        where("ownerId", "==", userId)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } else {
      const localStoreKey = localStorage.getItem(LOCAL_STORE_KEY) || "[]";
      const offlineData = JSON.parse(localStoreKey);
      return offlineData.filter((island: any) => island.ownerId === userId);
    }
  } catch (error) {
    console.error("[FlyingIslands] Fetch failed:", error);
    return [];
  }
};


export interface FlyingIsland {
  coords: { x: number; y: number; z: number };
  owner: string;
  lastIslandRewardClaimTimestamp: Timestamp;
}

export async function getOwnedIslands(
  userId: string
): Promise<Array<{ id: string } & FlyingIsland>> {
  const q = query(
    collection(db, "flyingIslands"),
    where("owner", "==", userId)
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...(doc.data() as FlyingIsland),
  }));
}

export async function getIslandById(
  islandId: string
): Promise<FlyingIsland | null> {
  const ref = doc(db, "flyingIslands", islandId);
  const snap = await getDoc(ref);
  return snap.exists() ? (snap.data() as FlyingIsland) : null;
}

// Auto-sync on reconnect
if (typeof window !== "undefined") {
  window.addEventListener("online", syncFlyingIslands);
}
